import { useEffect, useState } from "react"
import { getImminentThresholdInstant, isAppointmentImminent } from "./formatting"
import { useJoinLink } from "./useAppointments"
import type { UpcomingAppointment } from "./types"

const CLOCK_TICK_MS = 60 * 1000

/** `setTimeout` stores its delay in a signed 32-bit int; anything larger wraps and fires at once. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1

/**
 * One `now`, advanced by two timers. The interval alone is not enough: its boundaries align to *mount*,
 * not to the appointment, so a card mounted 30m10s out would cross the threshold ten seconds later but
 * not re-render for another fifty — stranding the member on `Complete forms` at the exact moment `Join`
 * should appear. Hence the one-shot timeout at the crossing.
 */
function useNow(crossesAt: Date | null): Date {
  const [now, setNow] = useState(() => new Date())
  // A primitive dep, so a fresh `Date` for the same instant doesn't reset the timers.
  const crossesAtMs = crossesAt?.getTime()

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), CLOCK_TICK_MS)
    // Skipped when the crossing is behind us, when there is no date (`NaN` fails both comparisons),
    // and when it is far enough out to overflow the timer.
    const delayMs = (crossesAtMs ?? NaN) - Date.now()
    const crossing = delayMs > 0 && delayMs <= MAX_TIMEOUT_MS ? setTimeout(() => setNow(new Date()), delayMs) : null

    return () => {
      clearInterval(tick)
      clearTimeout(crossing ?? undefined)
    }
  }, [crossesAtMs])

  return now
}

export type AppointmentTiming = {
  /** One reading, feeding both the countdown and the imminence test so they cannot disagree. */
  now: Date
  imminent: boolean
  /** The record's own link, else the just-in-time fetch. Empty when there is none yet. */
  joinUrl: string
  canJoin: boolean
}

/**
 * Shared by `UpNextCard` and `HomeAppointmentsTile`. The two render nothing alike but agree exactly on
 * when a visit becomes joinable and how its link resolves, which is the part worth having once.
 *
 * Tolerates a null appointment because both callers guard *after* their hooks run.
 */
export function useAppointmentTiming(appointment: UpcomingAppointment | null | undefined): AppointmentTiming {
  const now = useNow(getImminentThresholdInstant(appointment?.date))
  const imminent = isAppointmentImminent(appointment?.date, now)
  // A fixture carries its own link, so the fixture path fires no request.
  const { joinUrl: fetchedJoinUrl } = useJoinLink(appointment?.id ?? "", {
    enabled: imminent && !appointment?.joinUrl
  })

  const joinUrl = appointment?.joinUrl || fetchedJoinUrl || ""
  return { now, imminent, joinUrl, canJoin: joinUrl.length > 0 }
}
